import { useMemo } from "react";
import { useRecordContext } from "react-admin";
import { DocumentRead, FieldMapping, TargetSchema } from "@/types";
import { useSchemaDefinitions } from "@/hooks/useSchemaDefinitions";
import { useDocumentTransformationData } from "./useDocumentTransformationData";
import { SCHEMA_LABELS } from "../utils/reviewUtils";

export interface AppliedFieldMapping {
  detectedHeader: string;
  targetField: string;
  fieldLabel: string;
  fieldDescription?: string;
}

export interface AppliedSchemaMapping {
  schema: TargetSchema;
  schemaLabel: string;
  mappings: AppliedFieldMapping[];
}

interface UseAppliedFieldMappingResult {
  schemaMappings: AppliedSchemaMapping[];
  isLoading: boolean;
  hasData: boolean;
  templateName?: string;
  templateVersion?: string;
}

/**
 * Hook to retrieve the field mappings that were applied when a document was loaded.
 * Used in the AppliedMappingTab to show users what mappings were used.
 */
export const useAppliedFieldMapping = (): UseAppliedFieldMappingResult => {
  const documentRecord = useRecordContext<DocumentRead>();

  const { template, isLoading: transformationLoading } = useDocumentTransformationData({
    documentId: documentRecord?.id,
  });

  const { data: schemaDefinitions, loading: schemasLoading } = useSchemaDefinitions();

  const isLoading = transformationLoading || schemasLoading;

  const schemaMappings = useMemo((): AppliedSchemaMapping[] => {
    if (!template?.field_mappings || !schemaDefinitions) {
      return [];
    }

    const mappingsBySchema = new Map<TargetSchema, FieldMapping[]>();

    template.field_mappings.forEach((mapping) => {
      const existing = mappingsBySchema.get(mapping.target_schema) || [];
      existing.push(mapping);
      mappingsBySchema.set(mapping.target_schema, existing);
    });

    const result: AppliedSchemaMapping[] = [];

    mappingsBySchema.forEach((mappings, schema) => {
      const schemaFields = schemaDefinitions.schemas[schema]?.fields || [];

      const appliedMappings: AppliedFieldMapping[] = mappings
        .filter((m) => m.extraction_location?.key)
        .map((mapping) => {
          const fieldDef = schemaFields.find((f) => f.field === mapping.target_field);

          return {
            detectedHeader: mapping.extraction_location.key!,
            targetField: mapping.target_field,
            fieldLabel: fieldDef?.label || mapping.target_field,
            fieldDescription: fieldDef?.description,
          };
        })
        .sort((a, b) => a.detectedHeader.localeCompare(b.detectedHeader));

      if (appliedMappings.length > 0) {
        result.push({
          schema,
          schemaLabel: SCHEMA_LABELS[schema] || schema,
          mappings: appliedMappings,
        });
      }
    });

    return result.sort((a, b) => a.schemaLabel.localeCompare(b.schemaLabel));
  }, [template, schemaDefinitions]);

  return {
    schemaMappings,
    isLoading,
    hasData: schemaMappings.length > 0,
    templateName: template?.name,
    templateVersion: template?.version,
  };
};
